import $ from 'jquery';
import _ from 'underscore';
import Promise from 'bluebird';
import React from 'react';
import ReactDOM from 'react-dom';
import { BackboneSchema as backboneSchema } from '@bingads-webui/backbone-schema';
import { DependencyResolver } from '@bingads-webui-campaign/dependency-resolver';
import {
  AudienceCampaignErrorsView,
  AudienceStepBase,
  getBulkErrorsLogMessage,
  ImpressionTrackingUrlOptions,
  generateNameSuggestionSuffix,
} from '@bingads-webui-campaign/audience-campaign-shared';
import { InputControl } from '@bingads-webui/input-control';
import { RemainingCharsHint } from '@bingads-webui/remaining-chars-hint';
import { withUiBlock } from '@bingads-webui/uiblocker';
import template from './template.pug';
import { AlertReact } from './alert-react';

const ImpressionTrackingUrlsMaxCount = 2;
/**
 * saveAndExit event.
 *
 * @event CrudAudienceCampaign#saveAndExit
 * @type {Object}
 */

/**
 * CrudAudienceCampaign class that extends {AudienceStepBase} class
 * The class defines the section of the setting Campaign
 *
 * @fires CrudAudienceCampaign#saveAndExit
 * @class CrudAudienceCampaign
 * @extends {AudienceStepBase}
 */
export class CrudAudienceCampaign extends AudienceStepBase {
  /**
   * Initializes the {CrudAudienceCampaign} class.
   *
   * @param {Object} options - options for initializing the class.
   * @param {Object} options.viewContext - viewContext for the View.
   * @param {Object} options.perfMarker - perfMarker
   * @returns {undefined}
   */
  initialize(options) {
    if (options && options.perfMarker) {
      options.perfMarker.willInit();
    }
    this.renderMarker;
    this.perfMarker = options.perfMarker;
    this.isAdImpressions = options.isAdImpressions;
    this.options = options;
    this.viewContext = options.viewContext;
    this.wizardElement = options.wizardElement;
    this.i18n = DependencyResolver.get('component/i18n/loader!campaignui/feature/audience-campaign');
    this.i18nErrors = DependencyResolver.get('component/api-errors-campaign/index');
    this.isNextButtonEnabled = (
      !(this.viewContext.isReadOnlyUser || this.viewContext.CurrentAccountInActive)
    );

    const schema = DependencyResolver.get('$/schema!campaign/Campaign');
    const SchemaModel = backboneSchema(
      schema,
      this.i18n,
      ['Name']
    );

    this.model = new SchemaModel();
    this.logScenario = options.logScenario;
    this.activityLogger = this.logScenario.createCampaignSettings.create();
    this.isVideo = options.isVideo;
    this.isEnabledForVerifiedImpressionTrackingAtCampaignLevel =
      options.isEnabledForVerifiedImpressionTrackingAtCampaignLevel && this.isVideo;

    const config = DependencyResolver.get('component/config/index');
    this.permissions = config.get('Permissions');

    this.isCopyCampaignSettingsAudienceCampaignEnabled = _.result(this.permissions, 'IsCopyCampaignSettingsAudienceCampaignEnabled', false);
    this.isEnabledDeprecateManualCpcForAudienceCampaign = _.result(this.permissions, 'IsEnabledDeprecateManualCpcForAudienceCampaign', false);

    if (this.isCopyCampaignSettingsAudienceCampaignEnabled) {
      const CopyCampaignSettings = DependencyResolver.get('crud-campaign/view/copy-campaign-settings');
      this.copySettings = new CopyCampaignSettings({
        scenario: 'copy-audience-campaign-settings',
        activityLogger: this.activityLogger,
      });
      this.copySettings.on('copy.campaign', this.copyFromCampaign.bind(this));
    }

    this.initControls();
  }

  /**
   * Get the title of the step section.
   * This is automatically called from the wizard.
   *
   * @readonly
   * @returns {Object} returns object has a property text that is used for the title of the section.
   */
  get title() {
    return {
      text: this.i18n.getString('Audience_Campaign_Crud_Campaign_Title'),
    };
  }

  /**
   * Get the page title of the step section.
   * This is automatically called from the wizard.
   *
   * @readonly
   * @returns {Object} returns object has a property text that is used for the title of the section.
   */
  get pageTitle() {
    return {
      text: this.i18n.getString('Audience_Campaign_Crud_Campaign_PageTitle'),
    };
  }

  /**
   * Get the actions of the step section.
   * This is automatically called from the wizard.
   *
   * @readonly
   * @returns {Object} returns array that has information about the actions.
   */
  get actions() {
    const actions = [
      {
        name: 'next',
        displayText: this.i18n.getString('Audience_Campaign_Crud_Campaign_Save_Next'),
        enabled: this.isNextButtonEnabled,
      },
    ];

    if (this.viewContext.currentCampaign) {
      actions.push({
        name: 'done',
        enabled: this.isNextButtonEnabled,
        htmlAttributes: {
          class: 'btn btn-primary btn-exitWithSave',
        },
        method: 'saveAndExit',
        displayText: this.i18n.getString('Audience_Campaign_Crud_Campaign_Save_Exit'),
      });
    } else {
      actions.push({
        name: 'cancel',
        displayText: this.i18n.getString('Audience_Campaign_Cancel_Label'),
      });
    }

    return actions;
  }

  /**
   * Renders the view
   *
   * @returns {View} returns the view itself
   */
  render() {
    // View can be inherited, this only needs to use perfMarker of its own.
    if (this.options && this.options.perfMarker) {
      this.renderMarker =
      this.perfMarker.createChild('RenderCampaignCrud');

      this.renderMarker.willInit();
    }
    if (this.perfMarker.name === 'CampaignCrud') {
      this.perfMarker.willRender();
    }

    this.$el.html(template(this));

    if (this.isVideo) {
      this.updateVideoAlert();
    }
    if (this.isAdImpressions) {
      this.updateAdImpressionAlert();
    }
    this.$('.name-input-control-container').append(this.nameInput.render().$el);
    this.$('.remaining-chars-hint-container').append(this.nameInputRemainingCharsHint.render().$el);
    this.$('.errors-container').append(this.errorsView.render().$el);

    if (this.isCopyCampaignSettingsAudienceCampaignEnabled) {
      this.$('.copy-campaign-settings-container').append(this.copySettings.render().$el);
    }

    if (this.isEnabledForVerifiedImpressionTrackingAtCampaignLevel) {
      this.$('.impression-tracking-url-options-container').append(this.impressionTrackingUrlOptions.render().$el);
    }

    if (this.budget) {
      this.$('.budget').empty().append(this.budget.$el);
      this.budget.render();
    }

    return this;
  }

  updateVideoAlert() {
    ReactDOM.render(
      <AlertReact
        i18n={this.i18n}
        alertKey="Create_AudienceCampaign_Alert_Video"
        learnMoreId="app54794"
      />,
      this.$('.video-alert')[0]
    );
  }

  updateAdImpressionAlert() {
    ReactDOM.render(
      <AlertReact
        i18n={this.i18n}
        alertKey="Create_AudienceCampaign_Alert_AdImpressions"
        learnMoreId="app54794"
      />,
      this.$('.adimpression-alert')[0]
    );
  }

  /**
   * WillShow is called when the view is shown.
   * To support navigating back
   * we reinitialize the budget.
   *
   * @return {undefined}
   */
  willShow() {
    if (this.viewContext.currentCampaign
      && this.viewContext.currentCampaign.Budget
      && this.budget
      && this.budget.viewModel.isIndividualBudgetSelected()) {
      this.budget.budgetView.update({ Amount: this.viewContext.currentCampaign.Budget.Amount });
    }
    if (this.perfMarker.name === 'CampaignCrud') {
      this.perfMarker.done();
      // As we show the first section of the wizard, we consider the whole wizard done rendered.
      if (this.perfMarker.parentMarker) {
        this.perfMarker.parentMarker.done();
      }
      if (this.options && this.options.perfMarker && this.renderMarker) {
        this.renderMarker.done();
      }
    }
  }

  /**
   * It attempts to save and return true if the save is done succefully.
   *
   * @returns {boolean} - returns true if it is ready to go to the next step.
   */
  wizardShouldGoNext() {
    return this.save().then((isSuccessful) => {
      this.activityLogger.signal(`clicked next: ${isSuccessful}`, 'crud audience campaign');
      return isSuccessful;
    });
  }

  /**
   * Get the data that is used for the parameter for saving.
   *
   * @returns {Object} - object representing the data for the campaign to be saved.
   */
  getData() {
    const timeZoneMapper = DependencyResolver.get('component/timezonemapper/index');

    const params = this.model.toJSON();

    // TODO: (youngim) somehow model.set does not work with extra properties
    // ['Name', 'TimeZone', 'CampaignType', 'LanguageCode']);
    // This should be set with the model.
    params.TimeZone = timeZoneMapper.getAccountTimeZone();
    params.CampaignType = 'Audience';
    params.Languages = ['All'];
    params.CampaignSubType = this.isVideo ? 'AudienceVideoAds' : undefined;

    if (this.isEnabledForVerifiedImpressionTrackingAtCampaignLevel && this.IASTrackingInfo) {
      // eslint-disable-next-line max-len
      params.VerifiedTrackingData = this.IASTrackingInfo;
    }

    if (this.budget) {
      params.Budget = this.budget.getValues(params);
    }

    if (!this.viewContext.currentCampaign) {
      params.AdScheduleUseSearcherTimeZone = false;
    }

    return params;
  }

  /**
   * Save the campaign with the given setting.
   *
   * @returns {Promise} - returns promise of returning boolean value indicating
   * if the save process is succesful or not.
   */
  save() {
    if (!this.validate()) {
      return Promise.resolve(false);
    }

    const jsdata = DependencyResolver.get('component/js-data-campaign/index');
    const params = this.getData();

    let createOrUpdateTask;

    if (this.viewContext.currentCampaign) {
      params.Id = this.viewContext.currentCampaign.Id;
      createOrUpdateTask =
        jsdata.Campaign.update(params.Id, params, { adapter: 'bulk', cacheResponse: false });
    } else {
      if (this.isAdImpressions) {
        params.BiddingScheme = {
          Type: 'ManualCpm',
        };
      } else if (!this.isVideo && this.isEnabledDeprecateManualCpcForAudienceCampaign) {
        // when isEnabledDeprecateManualCpcForAudienceCampaign is enabled, we create the campaign with BiddingScheme eCpc
        // because we want to deprecate mCpc for audience campaign
        // the default BiddingScheme will be eCpc for audience campaign except for video/brandawareness audience campaigns
        // if UI pass mCpc to MT, MT will convert it into eCpc
        params.BiddingScheme = {
          Type: 'EnhancedCpc',
        };
      }
      createOrUpdateTask =
        jsdata.Campaign.create(params, { adapter: 'bulk', cacheResponse: false });
    }

    this.errorsView.removeErrors();

    return createOrUpdateTask.then((data) => {
      if (_.isFinite(data.Id)) {
        this.activityLogger.signal(`is simplify piloting account: ${this.permissions.IsNewStaticAudienceCampaignCreationEnabled} campaign Id: ${data.Id}, campaign Type: ${data.CampaignType}, campaign SubType: ${data.CampaignSubType}`, 'default campaign creation');
        // Market team ask ui to trigger an event when customer create a new audience ads campaign
        $(document).trigger('analytic_campaign_created', data);
        // for audience campaign, we need to get the bidding scheme information
        if (data.CampaignType === 'Audience') {
          return jsdata.Campaign.find(data.Id, {
            queryParameters: {
              query: {
                // Must select Id for jsdata cache
                $select: 'Id, BiddingScheme, CampaignType, CampaignSubType, CampaignSettings',
              },
            },
            bypassCache: false,
            cacheResponse: true,
          }).then((rawResponse) => {
            const campaign = {
              ...data,
              ...rawResponse,
            };
            this.viewContext.currentCampaign = campaign;
            return true;
          });
        }
        this.viewContext.currentCampaign = data;

        return true;
      }

      return false;
    }).catch((response) => {
      if (response && response.entity) {
        const hasErrors = response && !_.isUndefined(response.errors);

        if (hasErrors) {
          const campaignErrors = _.partition(
            response.errors,
            error => error.Type === 'Campaign Error'
          )[0];

          if (campaignErrors.length) {
            this.errorsView.setErrors(this.i18nErrors.getBulkApiErrors(campaignErrors));

            this.activityLogger.trace(getBulkErrorsLogMessage(campaignErrors));

            _.each(campaignErrors, (error) => {
              this.activityLogger.signal(error.Error, 'audience campaign creation api error');
            });
          }
        } else {
          // Unexpected case. Log Error.
          this.activityLogger.error(JSON.stringify(response));
        }
      } else {
        // Ajax errors
        this.errorsView.setErrors(this.i18nErrors.getODataErrors(response.errors));
        _.each(response.errors, (error) => {
          this.activityLogger.signal(error.Error, 'audience campaign creation api error');
        });
      }

      return false;
    }).finally(withUiBlock(this.wizardElement, this.i18n.getString('Loading')));
  }

  /**
   * Save the campaign and then exit the wizard.
   *
   * @fires CrudAudienceCampaign#saveAndExit
   * @returns {Promise} - returns promise of returning boolean value indicating
   * if the save process is succesful or not.
   */
  saveAndExit() {
    return this.save().then((isSuccessful) => {
      this.activityLogger.signal(`save and exit: ${isSuccessful}`, 'crud audience campaign');
      if (isSuccessful) {
        this.trigger('saveAndExit');
      }
    });
  }

  validate() {
    if (!this.nameInput.getState().isValid) {
      this.nameInput.setState({ showError: true });

      return false;
    }

    if (this.impressionTrackingUrlOptions && this.IASTrackingInfo === 'error') {
      this.impressionTrackingUrlOptions.setErrors([this.i18n.getString('IASImpressionTracking_Invalid')], 'VerifiedImpressionTracking_IAS');
      return false;
    }

    return this.budget.validate();
  }

  // private function
  // it initailizes the name input view, errors view and budget view
  initControls() {
    this.nameInput = new InputControl({
      entity: this.model,
      field: 'Name',
      required: true,
      ariaLabelledby: 'campaignNameLabel',
    });

    this.nameInput.on('input-changed', () => {
      this.activityLogger.signal('campaign name change', 'crud audience campaign');
    });

    const isAudienceCampaignCollapseTargetSelectionEnabled =
       _.result(this.permissions, 'IsAudienceCampaignCollapseTargetSelectionEnabled', false);

    if (isAudienceCampaignCollapseTargetSelectionEnabled && _.isEmpty(this.model.get('Name'))) {
      this.nameInput.setState({ value: generateNameSuggestionSuffix('Campaign') });
    }

    this.nameInputRemainingCharsHint = new RemainingCharsHint({
      i18n: this.i18n,
      model: this.model,
      field: 'Name',
    });

    if (this.isEnabledForVerifiedImpressionTrackingAtCampaignLevel) {
      this.impressionTrackingUrlOptions = new ImpressionTrackingUrlOptions({
        i18n: this.i18n,
        initialIASImpressionTrackingUrlValue: '',
        maxImpressionTrackingUrlsCount: ImpressionTrackingUrlsMaxCount,
        isImpressionTrackingUrlSectionVisible: false,
        isIASSectionVisible: true,
        isEnabledForVerifiedImpressionTrackingAtCampaignLevel:
          this.isEnabledForVerifiedImpressionTrackingAtCampaignLevel,
      });

      this.impressionTrackingUrlOptions.on('IASInputChange', (value) => {
        if ((_.isString(value) && value.length === 0)) {
          // update scenario
          if (this.viewContext.currentCampaign &&
            this.viewContext.currentCampaign.VerifiedTrackingData) {
            this.IASTrackingInfo = 'delete_value';
          } else {
            // create scenario
            this.IASTrackingInfo = undefined;
          }
        } else {
          this.IASTrackingInfo = value;
        }
      });
    }

    this.errorsView = new AudienceCampaignErrorsView();

    this.initBudgetView();
  }

  // private function
  // it initailizes the instance of the budget view.
  initBudgetView() {
    const SharedBudgetView = DependencyResolver.get('campaignui/feature/shared-budget/view/shared-budget-switch-editor');
    const Budget = DependencyResolver.get('campaignui/feature/budget/view/budget');

    const budgetFactory = SharedBudgetView.newFactory(Budget);

    this.budget = budgetFactory({
      model: _.isObject(this.model) ? this.model.budget : {},
      accountCurrencyCode: this.viewContext.accountCurrencyCode,
      activityLogger: this.activityLogger,
    });
  }

  copyFromCampaign(source) {
    const campaign = _.first(source);
    this.activityLogger.signal(`Copy setting from campaign: ${campaign.Name} ${campaign.Id}`, 'Audience Copy Campaign Setting');

    // copy budget setting
    const budget = _.result(campaign, 'Budget');
    if (_.isObject(budget) && this.budget &&
      (budget.Type === 'DailyStandard' || budget.Type === 'DailyAccelerated' || budget.Id > 0)
    ) {
      this.budget.update(budget);
    }

    // video audience campaign
    if (_.result(this.options, 'isVideo')) {
      // copy video impression tracking url
      if (this.isEnabledForVerifiedImpressionTrackingAtCampaignLevel) {
        const IASImpressionTrackingUrlValue = _.chain(campaign).result('CampaignSettings').first().result('VerifiedTrackingSettings')
          .first()
          .result('VerifiedTrackingData')
          .first()
          .result('Data')
          .result('Values')
          .value();

        if (_.isArray(IASImpressionTrackingUrlValue) && !_.isEmpty(IASImpressionTrackingUrlValue)) {
          this.impressionTrackingUrlOptions
            .setIASImpressionTrackingUrlValue(IASImpressionTrackingUrlValue);
        }
      }
    }
  }
}
